import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  SelectChangeEvent,
  IconButton,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add, Close } from '@mui/icons-material';
import { logger } from '../utils/logger';
import { useCollections } from '../hooks/useCollections';
import { getCatalogDisplayName } from '../utils/stringHelpers';
import { CollectionCreate } from '../types/collection';

interface AddToCollectionDialogProps {
  open: boolean;
  itemId: string;
  itemTitle: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddToCollectionDialog = ({
  open,
  itemId,
  itemTitle,
  onClose,
  onSuccess
}: AddToCollectionDialogProps) => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Albums for the catalog the object belongs to (active catalog), not the
  // caller's own albums when viewing a delegated catalog.
  const { activeCatalogCollections, addItemToCollection, fetchItemCollections, createCollection } = useCollections();
  const collections = activeCatalogCollections ?? [];
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentCollections, setCurrentCollections] = useState<string[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCollectionData, setNewCollectionData] = useState<CollectionCreate>({
    name: '',
    description: '',
    visibility: 'private',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (open && itemId) {
      fetchItemCollections(itemId)
        .then(items => setCurrentCollections(items.map(i => i.collectionId)))
        .catch(err => logger.error('Failed to fetch item collections', err));
    }
  }, [open, itemId, fetchItemCollections]);

  const hasNoCollections = collections.length === 0;

  const handleAdd = async () => {
    if (selectedCollections.length === 0) {
      setError('Please select at least one album');
      return;
    }

    const newCollectionIds = selectedCollections.filter(
      id => !currentCollections.includes(id)
    );

    if (newCollectionIds.length === 0) {
      setError('This item is already in the selected album(s)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await Promise.all(
        newCollectionIds.map(collectionId =>
          addItemToCollection(collectionId, itemId)
        )
      );
      onSuccess();
      handleClose();
    } catch (err) {
      setError('Failed to add item to album');
      logger.error('Failed to add item to collections', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCollections([]);
    setError(null);
    setShowCreateForm(false);
    setNewCollectionData({ name: '', description: '', visibility: 'private' });
    setCreateError(null);
    onClose();
  };

  const handleSelectChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedCollections(event.target.value as string[]);
  };

  const handleCreateCollection = async () => {
    if (!newCollectionData.name.trim()) {
      setCreateError('Album name is required');
      return;
    }

    setCreateLoading(true);
    setCreateError(null);

    try {
      await createCollection(newCollectionData);
      setShowCreateForm(false);
      setNewCollectionData({ name: '', description: '', visibility: 'private' });
    } catch (err) {
      setCreateError('Failed to create album');
      logger.error('Failed to create collection', err);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={fullScreenMobile}>
      <DialogTitle sx={{ position: 'relative', pr: 5 }}>
        Add to Album: &quot;{itemTitle}&quot;
        <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {hasNoCollections && !showCreateForm && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                You don&apos;t have any albums yet. Create one to start organizing your items.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setShowCreateForm(true)}
              >
                Create Album
              </Button>
            </Box>
          )}

          {showCreateForm && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Create New Album
              </Typography>
              <TextField
                label="Album Name"
                value={newCollectionData.name}
                onChange={(e) => setNewCollectionData({ ...newCollectionData, name: e.target.value })}
                fullWidth
                required
                sx={{ mb: 2 }}
                inputProps={{ 'aria-label': 'Album Name' }}
              />
              <TextField
                label="Description (optional)"
                value={newCollectionData.description}
                onChange={(e) => setNewCollectionData({ ...newCollectionData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />
              {createError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {createError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => setShowCreateForm(false)} size="small">
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateCollection}
                  disabled={createLoading}
                  size="small"
                >
                  {createLoading ? 'Creating...' : 'Create'}
                </Button>
              </Box>
            </Box>
          )}

          {!hasNoCollections && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Albums</InputLabel>
              <Select
                multiple
                value={selectedCollections}
                onChange={handleSelectChange}
                label="Select Albums"
              >
                {collections.map(collection => {
                  const isAlreadyMember = currentCollections.includes(collection.collectionId);
                  return (
                    <MenuItem key={collection.collectionId} value={collection.collectionId}>
                      {getCatalogDisplayName(collection.name)} ({collection.itemCount} items)
                      {isAlreadyMember && ' — already added'}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {!hasNoCollections && (
          <Button
            onClick={handleAdd}
            variant="contained"
            disabled={loading || selectedCollections.length === 0}
          >
            {loading ? 'Adding...' : 'Add'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
